import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { CloudinaryImage } from '@cloudinary/url-gen';
import { lazyload, responsive } from '@cloudinary/react';
import { useTranslation } from 'react-i18next';

// Types
import { Props } from './types';

// Utils
import { buildCldImage, buildLqipUrl } from '../../../utils/cloudinary';

// Styled Components
import {
  Container,
  CloudinaryAdvanceImage,
  ImageFrame,
  LqipLayer,
  Text,
  ContainerInfo,
  TextContainer,
  StyledDefinition,
  CheckboxContainer,
  DeleteContainer,
  DeleteIconImg,
} from './styles';

// Components
import { Loader } from '../../inputs/Loader';
import RemoveIcon from '../../../assets/icons/remove-icon.svg';
import { CheckBox } from '../../inputs/CheckBox';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

/**
 * Migración Hetzner: la imagen se reconstruye con el SDK Cloudinary vía
 * `buildCldImage`, que soporta tanto Hetzner (`publicUrl` → fetch type) como
 * legacy (`cloudinaryPublicId` → public_id). El backend ya no firma URLs.
 *
 * Identidad de selección: `id` (Image record id estable) — NUNCA
 * `cloudinaryPublicId`, que es null para imágenes servidas desde Hetzner.
 */
export const Photo = ({
  id,
  image,
  imageUrl,
  title,
  date,
  roid,
  isSelected = false,
  onSelect,
  selectionMode = false,
  onDelete,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const parsedDate = typeof date === 'string' ? new Date(date) : date;
  const currentDate = format(parsedDate, 'dd / MM / yyyy');
  const currentHours = format(parsedDate, 'HH');
  const currentMinutes = format(parsedDate, 'mm');
  const dispatch = useDispatch();

  const cloudinaryImageSrc: CloudinaryImage | null = useMemo(() => {
    const built = buildCldImage(image);
    // Sin transformación esto servía el ORIGINAL para pintar una miniatura:
    // una rejilla de 20 fotos se descargaba decenas de MB. `c_limit` no
    // amplía, así que en pantalla no se nota ninguna diferencia. El visor a
    // pantalla completa y las descargas SÍ siguen sirviendo el original.
    built?.addTransformation('c_limit,w_1024,h_1024,q_auto:low,f_auto');
    return built;
  }, [image]);

  // Placeholder blur-up: URL diminuta sharp; el blur lo hace el navegador
  // (CSS) — ver LqipLayer en styles. Reemplaza al plugin placeholder(), que
  // difuminaba a resolución completa en servidor (lento + add-on premium).
  const lqipUrl: string | null = useMemo(() => buildLqipUrl(image), [image]);

  const handleAddImage = () => {
    // If selection mode is active, toggle selection instead of opening modal
    if (selectionMode && onSelect) {
      onSelect(id);
      return;
    }

    dispatch(
      openModal(modalIds.VIEW_FULLSCREEN_IMAGE, {
        // Resolved string URL — used by the download handler.
        currentImage: imageUrl,
        // Resolvable fields — the modal rebuilds the CloudinaryImage for
        // <AdvancedImage> via buildCldImage (Hetzner publicUrl / legacy id).
        image: image,
        title: title,
        currentDate: currentDate,
        currentHours: currentHours,
        currentMinutes: currentMinutes,
        roid: roid,
        publicId: '',
      })
    );
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(id);
    }
  };

  return (
    <Container onClick={handleAddImage} isSelected={isSelected}>
      {selectionMode && (
        <CheckboxContainer onClick={handleCheckboxClick}>
          <CheckBox checked={isSelected} indeterminate={false} readOnly />
        </CheckboxContainer>
      )}
      {/* Papelera: solo si el consumidor la pide (la card del tramo, y solo a
          quien tiene permiso). `stopPropagation` para que tocarla NO abra
          además la foto a pantalla completa. */}
      {onDelete && !selectionMode && (
        <DeleteContainer
          data-testid="photo-delete"
          title={t('common:buttons.delete')}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(id);
          }}
        >
          <DeleteIconImg src={RemoveIcon} alt={t('common:deleteIcon')} />
        </DeleteContainer>
      )}
      {cloudinaryImageSrc ? (
        <ImageFrame>
          {lqipUrl && <LqipLayer src={lqipUrl} alt="" aria-hidden="true" />}
          <CloudinaryAdvanceImage
            cldImg={cloudinaryImageSrc}
            plugins={[
              lazyload({
                rootMargin: '5px 5px 5px 5px',
                threshold: 0.15,
              }),
              responsive({
                steps: [479, 639, 1162],
              }),
              // SIN plugin placeholder(): difuminaba a resolución completa en
              // servidor (~2s cómputo + el default 'vectorize' era add-on
              // premium). El placeholder ahora es blur-up: LqipLayer (w_32) +
              // CSS blur en el navegador, detrás de esta imagen real.
            ]}
          />
        </ImageFrame>
      ) : (
        <Loader />
      )}
      <TextContainer>
        <Text colorText="greyDark" size="XS">
          {title}
        </Text>
        <ContainerInfo>
          <StyledDefinition
            title={t('common:creationDate')}
            value={currentDate}
          />
          <StyledDefinition
            title={t('common:creationTime')}
            value={`${currentHours}:${currentMinutes}`}
          />
          <StyledDefinition title="ROID" value={roid} />
        </ContainerInfo>
      </TextContainer>
    </Container>
  );
};
